/**
 * CurrencyContext — global currency preference with live conversion.
 *
 * Stores the user's preferred display currency in localStorage and provides
 * `formatMoney(amount, baseCurrency?)` for converting + formatting any value.
 *
 * Supports 18 Pan-African + international currencies with live FX rates.
 */
import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

const STORAGE_KEY = "md_preferred_currency";

/**
 * All supported currencies, grouped logically.
 * Order: KES (home) → Global → Pan-African sovereign → Regional unions → EAC → Hub
 */
const SUPPORTED = [
    "KES", "USD", "EUR", "GBP",
    "NGN", "ZAR", "EGP", "MAD", "GHS",
    "XOF", "XAF",
    "TZS", "UGX", "RWF", "ETB",
    "MUR", "ZMW", "BWP",
];

/** Display metadata for the currency picker */
const CURRENCY_META = {
    KES: { symbol: "KSh",  name: "Kenyan Shilling",       flag: "🇰🇪", group: "Home" },
    USD: { symbol: "$",    name: "US Dollar",              flag: "🇺🇸", group: "Global" },
    EUR: { symbol: "€",    name: "Euro",                   flag: "🇪🇺", group: "Global" },
    GBP: { symbol: "£",    name: "British Pound",          flag: "🇬🇧", group: "Global" },
    NGN: { symbol: "₦",    name: "Nigerian Naira",         flag: "🇳🇬", group: "West Africa" },
    ZAR: { symbol: "R",    name: "South African Rand",     flag: "🇿🇦", group: "Southern Africa" },
    EGP: { symbol: "E£",   name: "Egyptian Pound",         flag: "🇪🇬", group: "North Africa" },
    MAD: { symbol: "MAD",  name: "Moroccan Dirham",        flag: "🇲🇦", group: "North Africa" },
    GHS: { symbol: "GH₵",  name: "Ghanaian Cedi",          flag: "🇬🇭", group: "West Africa" },
    XOF: { symbol: "CFA",  name: "CFA Franc BCEAO",        flag: "🏦", group: "Regional Union" },
    XAF: { symbol: "FCFA", name: "CFA Franc BEAC",         flag: "🏦", group: "Regional Union" },
    TZS: { symbol: "TSh",  name: "Tanzanian Shilling",     flag: "🇹🇿", group: "East Africa" },
    UGX: { symbol: "USh",  name: "Ugandan Shilling",       flag: "🇺🇬", group: "East Africa" },
    RWF: { symbol: "RF",   name: "Rwandan Franc",          flag: "🇷🇼", group: "East Africa" },
    ETB: { symbol: "Br",   name: "Ethiopian Birr",         flag: "🇪🇹", group: "East Africa" },
    MUR: { symbol: "Rs",   name: "Mauritian Rupee",        flag: "🇲🇺", group: "Southern Africa" },
    ZMW: { symbol: "ZK",   name: "Zambian Kwacha",         flag: "🇿🇲", group: "Southern Africa" },
    BWP: { symbol: "P",    name: "Botswana Pula",          flag: "🇧🇼", group: "Southern Africa" },
};

// Fallback rates: 1 unit of currency → KES
const FALLBACK_RATES = {
    KES: 1,
    USD: 129.5,
    EUR: 151.1,
    GBP: 176.5,
    NGN: 0.0847,
    ZAR: 7.14,
    EGP: 2.63,
    MAD: 13.16,
    GHS: 8.33,
    XOF: 0.212,
    XAF: 0.212,
    TZS: 0.0513,
    UGX: 0.0351,
    RWF: 0.098,
    ETB: 1.02,
    MUR: 2.78,
    ZMW: 4.76,
    BWP: 9.52,
};

// ─── Live singleton ───────────────────────────────────────────────────
// Non-component modules (lib/api.js's fmtKES, numberColor.js, chart
// tooltips) can't call the useCurrency() hook. This module-level mirror
// keeps them reading the SAME single source of truth as every
// <CurrencyValue>/useCurrency() consumer, kept fresh by CurrencyProvider
// below on every currency/rate change.
let _live = { currency: "KES", rates: FALLBACK_RATES };
export function getLiveCurrencySnapshot() {
    return _live;
}
export { CURRENCY_META, SUPPORTED };

function _readCsrfCookie() {
    if (typeof document === "undefined") return null;
    const raw = document.cookie || "";
    for (const chunk of raw.split(";")) {
        const [k, ...v] = chunk.trim().split("=");
        if (k === "md_csrf") return decodeURIComponent(v.join("="));
    }
    return null;
}

// Persists the chosen currency to the user's profile so backend-generated
// content (WhatsApp, notifications, risk-gate messages) renders in the
// SAME currency the header toggle shows — the toggle is the single source
// of truth for both frontend and backend. Best-effort: silently no-ops for
// logged-out visitors or transient network errors.
function _syncCurrencyToBackend(currency) {
    const API = process.env.REACT_APP_BACKEND_URL || "";
    const csrf = _readCsrfCookie();
    fetch(`${API}/api/preferences`, {
        method: "PATCH",
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ preferred_display_currency: currency }),
    }).catch(() => { /* best-effort — UI already updated optimistically */ });
}

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
    const [currency, setCurrencyState] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return SUPPORTED.includes(saved) ? saved : "KES";
        } catch {
            return "KES";
        }
    });

    // Live rates: { KES: 1, USD: 129.5, ... } (1 unit → KES)
    const [rates, setRates] = useState(FALLBACK_RATES);
    const fetched = useRef(false);

    useEffect(() => {
        if (fetched.current) return;
        fetched.current = true;
        const API = process.env.REACT_APP_BACKEND_URL || "";
        fetch(`${API}/api/fx/live`, { credentials: "include" })
            .then((r) => r.ok ? r.json() : Promise.reject())
            .then((data) => {
                const live = data?.rates;
                if (live && Object.keys(live).length > 1) {
                    setRates((prev) => ({ ...prev, ...live }));
                }
            })
            .catch(() => { /* use fallback */ });
    }, []);

    // Keep the non-hook singleton mirror fresh on every currency/rate change
    // so fmtKES() (api.js) and every plain-JS caller stay in lock-step with
    // this context — the header toggle is the ONE source of truth.
    useEffect(() => {
        _live = { currency, rates };
    }, [currency, rates]);

    const setCurrency = useCallback((c) => {
        const valid = SUPPORTED.includes(c) ? c : "KES";
        setCurrencyState(valid);
        try { localStorage.setItem(STORAGE_KEY, valid); } catch { /* ignore */ }
        _syncCurrencyToBackend(valid);
    }, []);

    const cycleCurrency = useCallback(() => {
        setCurrencyState((prev) => {
            const idx = SUPPORTED.indexOf(prev);
            const next = SUPPORTED[(idx + 1) % SUPPORTED.length];
            try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
            _syncCurrencyToBackend(next);
            return next;
        });
    }, []);

    /**
     * Convert and format a monetary value.
     * @param {number} amount — the raw numeric value
     * @param {string} [baseCurrency="KES"] — what currency the amount is already in
     * @returns {string} — formatted string like "USD 12.50" or "KES 1,620"
     */
    const formatMoney = useCallback(
        (amount, baseCurrency = "KES") => {
            if (amount === null || amount === undefined || isNaN(amount)) return "\u2014";
            const num = Number(amount);
            const base = baseCurrency.toUpperCase();
            const target = currency;

            // Convert: amount_in_base → KES → target
            const toKES = base === "KES" ? num : num * (rates[base] || 1);
            const converted = target === "KES" ? toKES : toKES / (rates[target] || 1);

            const sign = converted < 0 ? "-" : "";
            const abs = Math.abs(converted);

            const meta = CURRENCY_META[target];
            const sym = meta?.symbol || target;

            const formatted = abs.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });

            return `${sign}${sym} ${formatted}`;
        },
        [currency, rates],
    );

    return (
        <CurrencyContext.Provider
            value={{ currency, setCurrency, cycleCurrency, formatMoney, rates, SUPPORTED, CURRENCY_META }}
        >
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    const ctx = useContext(CurrencyContext);
    if (!ctx) {
        return {
            currency: "KES",
            setCurrency: () => {},
            cycleCurrency: () => {},
            formatMoney: (n) => {
                if (n === null || n === undefined || isNaN(n)) return "\u2014";
                return `KSh ${Math.abs(Number(n)).toLocaleString("en-KE", { maximumFractionDigits: 2 })}`;
            },
            rates: FALLBACK_RATES,
            SUPPORTED,
            CURRENCY_META,
        };
    }
    return ctx;
}
